import { useQuery } from '@apollo/client';
import { buttonV2Class } from '@synoptic/ui-kit/button-v2/button-v2.css.js';
import { CardV2 } from '@synoptic/ui-kit/card-v2/card-v2.js';
import { ArrowRightIcon } from '@synoptic/ui-kit/icons/react/arrow-right.js';
import { SearchInputV2 } from '@synoptic/ui-kit/input-v2/search-input-v2.js';
import { SkeletonV2 } from '@synoptic/ui-kit/skeleton-v2/skeleton-v2.js';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import {
  ToggleGroupV2,
  ToggleV2,
} from '@synoptic/ui-kit/toggle-v2/toggle-v2.js';
import { useId, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import {
  ExploreDataStreamsSearchQueryQuery,
  StreamCardFragmentFragment,
  StreamCategory,
  StreamSortBy,
} from '@/__generated__/graphql.ts';
import { gql } from '@/__generated__/index.ts';
import { StreamRowLinkV2 } from '@/components/stream-row-v2/stream-row-link-v2.tsx';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import {
  exploreDataStreamFiltersV2,
  exploreDataStreamRowsV2,
  exploreDataStreamRowSkeletonV2,
  exploreDataStreamsListV2,
  exploreSectionHeaderV2,
  exploreSectionHeadingV2,
  exploreSectionSubtitleV2,
  exploreSectionTitleV2,
  exploreSectionV2,
} from './explore-page-v2.css.ts';

const ExploreDataStreamsQuery = gql(`
  query ExploreDataStreamsQuery(
    $streamCategory: StreamCategory
    $sortBy: StreamSortBy
  ) {
    streamsAll(
      limit: 10
      custom: false
      nextToken: null
      streamCategory: $streamCategory
      sortBy: $sortBy
    ) {
      items {
        ...StreamCardFragment
      }
      nextToken
    }
  }
`);

const ExploreDataStreamsSearchQuery = gql(`
  query ExploreDataStreamsSearchQuery(
    $query: String!
    $streamCategory: StreamCategory
  ) {
    search(
      input: {
        limit: 10
        offset: 0
        text: $query
        filters: {
          type: [Stream]
          stream: { streamCategory: $streamCategory, custom: false }
        }
      }
    ) {
      items {
        ...StreamCardFragment
      }
      offset
    }
  }
`);

const DATA_STREAM_CATEGORIES = [
  { title: 'All', value: 'all' },
  { title: 'OSINT', value: StreamCategory.Osint },
  { title: 'Regulatory', value: StreamCategory.Regulatory },
  { title: 'Markets', value: StreamCategory.Markets },
  { title: 'News', value: StreamCategory.News },
  { title: 'Onchain', value: StreamCategory.OnChain },
] as const;

const DATA_STREAM_SORTS = [
  { title: 'All', value: 'all' },
  { title: 'Popular', value: 'popular' },
  { title: 'New', value: 'new' },
] as const;

type DataStreamCategoryValue = (typeof DATA_STREAM_CATEGORIES)[number]['value'];
type DataStreamSortValue = (typeof DATA_STREAM_SORTS)[number]['value'];

const toStreamCategory = (
  category: DataStreamCategoryValue,
): StreamCategory | null => (category === 'all' ? null : category);

const toStreamSortBy = (sort: DataStreamSortValue): StreamSortBy | null => {
  switch (sort) {
    case 'all':
      return null;
    case 'popular':
      return StreamSortBy.Followers;
    case 'new':
      return StreamSortBy.CreatedAt;
    default: {
      const exhaustive: never = sort;
      return exhaustive;
    }
  }
};

const SKELETON_COUNT = 10;

const renderSkeletons = () =>
  Array.from({ length: SKELETON_COUNT }, (_, index) => (
    <SkeletonV2
      key={index}
      className={exploreDataStreamRowSkeletonV2}
      height={62}
      width="100%"
    />
  ));

const streamsFromSearch = (
  items: ExploreDataStreamsSearchQueryQuery['search']['items'] | undefined,
) =>
  items?.filter(
    (item): item is StreamCardFragmentFragment => item.__typename === 'Stream',
  );

const renderRows = (streams: StreamCardFragmentFragment[]) =>
  streams
    .slice(0, 10)
    .map((stream) => <StreamRowLinkV2 key={stream.id} stream={stream} />);

export const ExploreDataStreamsSectionV2 = () => {
  const titleId = useId();
  const searchId = useId();
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<DataStreamCategoryValue>('all');
  const [sort, setSort] = useState<DataStreamSortValue>('all');
  const [debouncedQuery] = useDebounce(query.trim(), 200);
  const isSearch = debouncedQuery.length > 0;
  const streamCategory = toStreamCategory(category);
  const sortBy = toStreamSortBy(sort);

  const catalog = useQuery(ExploreDataStreamsQuery, {
    skip: isSearch,
    variables: {
      streamCategory,
      sortBy,
    },
    fetchPolicy: 'cache-and-network',
    onError: () => {
      toast.error({
        title: 'Failed to fetch streams',
      });
    },
  });

  const search = useQuery(ExploreDataStreamsSearchQuery, {
    skip: !isSearch,
    variables: {
      query: debouncedQuery,
      streamCategory,
    },
    fetchPolicy: 'cache-and-network',
    onError: () => {
      toast.error({
        title: 'Failed to fetch streams',
      });
    },
  });

  const resolved = isSearch
    ? search.data
      ? (streamsFromSearch(search.data.search?.items) ?? [])
      : undefined
    : catalog.data
      ? (catalog.data.streamsAll?.items ?? [])
      : undefined;
  const streamsRef = useRef(resolved);
  if (resolved !== undefined) {
    streamsRef.current = resolved;
  }
  const streams = resolved ?? streamsRef.current;
  const loading = isSearch ? search.loading : catalog.loading;

  return (
    <section className={exploreSectionV2} aria-labelledby={titleId}>
      <header className={exploreSectionHeaderV2}>
        <div className={exploreSectionHeadingV2}>
          <h2 id={titleId} className={exploreSectionTitleV2}>
            Data Streams
          </h2>
          <p className={exploreSectionSubtitleV2}>
            Data Streams is an automated, high-volume content feed that can be
            customized for various applications.
          </p>
        </div>
        <Link className={buttonV2Class.secondary} to={paths.streams}>
          See all streams
          <ArrowRightIcon />
        </Link>
      </header>
      <CardV2 className={exploreDataStreamsListV2}>
        <SearchInputV2
          id={searchId}
          aria-label="Search streams"
          placeholder="Search streams — tariffs, court filings, crypto..."
          value={query}
          onQueryChange={setQuery}
          loading={loading && streams !== undefined}
          spellCheck={false}
        />
        <div className={exploreDataStreamFiltersV2}>
          <ToggleGroupV2
            variant="outline"
            value={category}
            onValueChange={setCategory}
            aria-label="Stream category"
          >
            {DATA_STREAM_CATEGORIES.map((item) => (
              <ToggleV2 key={item.value} value={item.value}>
                {item.title}
              </ToggleV2>
            ))}
          </ToggleGroupV2>
          <ToggleGroupV2
            variant="segmented"
            value={sort}
            onValueChange={setSort}
            aria-label="Stream sort"
          >
            {DATA_STREAM_SORTS.map((item) => (
              <ToggleV2 key={item.value} value={item.value}>
                {item.title}
              </ToggleV2>
            ))}
          </ToggleGroupV2>
        </div>
        <div className={exploreDataStreamRowsV2}>
          {streams === undefined ? renderSkeletons() : renderRows(streams)}
        </div>
      </CardV2>
    </section>
  );
};
